import * as React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'

import Group from 'components/utils/Group'
import IconButton from 'LuxKit/Button/IconButton'
import TextButton from 'LuxKit/Button/TextButton'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import Heading from 'LuxKit/Typography/Heading'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import StickyPrompt from 'components/Common/StickyPromptCard/StickyPrompt'
import VoucherCarouselCard, { type VoucherCarouselCardVoucher } from 'marketing/components/VoucherCarouselCard'
import getVoucherProductTypeIcon from 'lib/voucher/getVoucherProductTypeIcon'
import { mediaQueryUp } from 'components/utils/breakpoint'
import zIndex from 'styles/tools/z-index'

const Wrapper = styled.div`
  position: relative;
  z-index: ${zIndex.promoDisplayConfig};
`

const CloseButton = styled(IconButton)`
  position: absolute;
  inset-inline-end: ${rem(20)};
  top: ${rem(20)};
`

const Prompt = styled(StickyPrompt)`
  padding: ${rem(24)};

  ${mediaQueryUp.tablet} {
    width: ${rem(380)};
  }
`

const HeaderIconWrapper = styled.div`
  position: relative;
  width: ${rem(100)};
  height: ${rem(100)};
  display: flex;
  align-items: center;
  justify-content: center;
`

const IconCircle = styled.div`
  width: ${rem(80)};
  height: ${rem(80)};
  border-radius: 50%;
  background-color: ${props => props.theme.palette.messaging.success.lightBackground};
  display: flex;
  align-items: center;
  justify-content: center;
`

const Sparkle = styled.div<{ $size: string; $top?: string; $left?: string; $right?: string; $bottom?: string }>`
  position: absolute;
  width: ${props => props.$size};
  height: ${props => props.$size};
  top: ${props => props.$top};
  left: ${props => props.$left};
  right: ${props => props.$right};
  bottom: ${props => props.$bottom};
  background-color: ${props => props.theme.palette.messaging.success.normalForeground};
  clip-path: polygon(50% 0%, 60% 40%, 100% 50%, 60% 60%, 50% 100%, 40% 60%, 0% 50%, 40% 40%);
`

const CentredBodyText = styled(BodyTextBlock)`
  text-align: center;
`

const FullWidthVoucherCard = styled(VoucherCarouselCard)`
  width: 100%;
`

export interface VoucherDisplayModelProps {
  show: boolean
  promptRef: React.RefObject<HTMLDivElement | null>
  voucher: VoucherCarouselCardVoucher
  displayMode?: 'appear_once' | 'persistence'
  expiryText?: string
  onClose: () => void
  onDismiss: () => void
  onViewVouchers: () => void
  onVoucherDetails?: () => void
}

function VoucherDisplayModel({
  show,
  promptRef,
  voucher,
  displayMode = 'appear_once',
  expiryText,
  onClose,
  onDismiss,
  onViewVouchers,
  onVoucherDetails,
}: VoucherDisplayModelProps) {
  const intl = useIntl()
  const VoucherIcon = getVoucherProductTypeIcon(voucher.productType)

  return (
    <Wrapper>
      <Prompt show={show} sticky={false} ref={promptRef}>
        <CloseButton kind="tertiary" shape="square" onClick={onClose}>
          <LineTimesIcon />
        </CloseButton>
        <Group direction="vertical" gap={16} fullWidth>
          <Group direction="vertical" gap={16} horizontalAlign="center" fullWidth>
            <HeaderIconWrapper>
              <IconCircle>
                <VoucherIcon size={40} colour="dark-contrast" />
              </IconCircle>
              <Sparkle $size={rem(14)} $top={rem(4)} $left={rem(6)} />
              <Sparkle $size={rem(10)} $bottom={rem(8)} $left={rem(14)} />
              <Sparkle $size={rem(10)} $top={rem(8)} $right={rem(6)} />
              <Sparkle $size={rem(8)} $top="50%" $right={rem(0)} />
            </HeaderIconWrapper>
            <Heading variant="heading3">
              {intl.formatMessage({
                id: 'N9Rw6QOGw6jg2oxanTJC',
                defaultMessage: 'You have a New Voucher!',
                description: 'Title shown at the top of the LE Convert voucher popup modal.',
              })}
            </Heading>
          </Group>
          <FullWidthVoucherCard
            variant="mobile"
            voucher={voucher}
            expiryText={expiryText}
            onDetailsClick={onVoucherDetails}
          />
          <CentredBodyText variant="medium">
            {intl.formatMessage({
              id: 'ZMGA8orSm2r-STJghxiR',
              defaultMessage: 'Voucher will automatically apply at checkout for eligible bookings.',
              description: 'Body text explaining how the LE Convert voucher popup works.',
            })}
          </CentredBodyText>
          <TextButton kind="primary" onClick={onViewVouchers}>
            {intl.formatMessage({
              id: 'AADi178MiRnMvWkV2A7F',
              defaultMessage: 'View Voucher Wallet',
              description: 'Primary CTA in the LE Convert voucher popup modal linking to the vouchers page.',
            })}
          </TextButton>
          {displayMode === 'appear_once' && (
            <TextButton kind="secondary" onClick={onDismiss}>
              {intl.formatMessage({
                id: 'jMxAkBtYLb_lWt0_3e5N',
                defaultMessage: 'I do not want to see this again.',
                description: 'Dismiss button in the LE Convert voucher popup modal.',
              })}
            </TextButton>
          )}
        </Group>
      </Prompt>
    </Wrapper>
  )
}

export default React.memo(VoucherDisplayModel)
